import { Injectable } from '@angular/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select } from '@ngxs/store';
import {
  AppointmentsHttpService,
  EventSubHttpService,
  FreeSlotsResult,
} from '@sc/data-access-http-services';
import {
  Appointment,
  AppointmentSuggestionsApi,
  AppointmentsFilterData,
  BookAppointmentApi,
  CancelAppointmentApi,
  RequestAppointmentApi,
} from '@sc/data-access-models';
import { Observable, of, throwError } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  switchMapTo,
} from 'rxjs/operators';
import {
  AppointmentsAddedAction,
  AppointmentsClearedAction,
  AppointmentsFilterAddedAction,
  AppointmentsFilterState,
  AppointmentsRemoveAction,
  AppointmentsState,
} from '../../store';
import { DossierSandboxService } from '../dossier';
import { SandboxService } from '../sandbox.service';

@Injectable({
  providedIn: 'root',
})
export class AppointmentsSandboxService {
  get statesComplete() {
    return ['BOOKED', 'NO_SHOW', 'CANCELLED'];
  }

  @Select(AppointmentsState)
  public appointments$: Observable<Appointment[]>;
  public outsideAppointment$: Observable<Appointment[]>;

  @Select(AppointmentsFilterState)
  public appointmentsFilter$: Observable<AppointmentsFilterData>;

  constructor(
    private readonly appointmentsHttpService: AppointmentsHttpService,
    private readonly eventSubHttpService: EventSubHttpService,
    private readonly dossierSB: DossierSandboxService,
    private readonly sandbox: SandboxService
  ) {
    this.outsideAppointment$ = this.appointments$.pipe(
      map((appoitments: Appointment[]) =>
        appoitments.filter((app) => app.outsideIntervention)
      )
    );
  }

  @Dispatch()
  public addAppointments(
    appointments: Appointment[],
    merge = true
  ): Observable<AppointmentsAddedAction> {
    return of(new AppointmentsAddedAction(appointments, merge));
  }

  @Dispatch()
  public clearAppointments(): Observable<AppointmentsClearedAction> {
    return of(new AppointmentsClearedAction());
  }

  @Dispatch()
  public removeAppointments(
    appointmentsToRemove: string[]
  ): Observable<AppointmentsRemoveAction> {
    return of(new AppointmentsRemoveAction(appointmentsToRemove));
  }

  public bookAppointment(params: BookAppointmentApi) {
    return this.appointmentsHttpService.bookAppointment(params).pipe(
      switchMap((response: any) =>
        this.dossierSB
          .setDossier(response.dossier)
          .pipe(map((dossier) => response))
      ),
      catchError((err) =>
        this.removeAppointments([params.dossier.appointment.id]).pipe(
          switchMapTo(throwError(err))
        )
      )
    );
  }

  public requestAppointment(params: RequestAppointmentApi) {
    return this.appointmentsHttpService
      .requestAppointment(params)
      .pipe(
        switchMap((response) =>
          this.dossierSB
            .setDossier(response.dossier)
            .pipe(map((dossier) => response))
        )
      );
  }

  public modifyAppointment(params: BookAppointmentApi) {
    return this.appointmentsHttpService.modifyAppointment(params).pipe(
      switchMap((response) =>
        this.dossierSB
          .setDossier(response.dossier)
          .pipe(map((dossier) => response))
      ),
      catchError((err) =>
        this.removeAppointments([params.dossier.appointment.id]).pipe(
          switchMapTo(throwError(err))
        )
      )
    );
  }

  public cancelAppointment(params: CancelAppointmentApi) {
    return this.appointmentsHttpService.cancelAppointment(params);
  }

  public subscribeToFreeSlotsResults(): Observable<FreeSlotsResult> {
    return this.eventSubHttpService
      .subscribeToFreeSlotsResults()
      .pipe(filter(() => true));
  }

  async waitReadyness(): Promise<boolean> {
    return this.eventSubHttpService.waitReadyness();
  }

  public fetchFreeSlots(params: any) {
    return this.appointmentsHttpService.fetchFreeSlots(params);
  }

  sendAppointmentSuggestions(params: AppointmentSuggestionsApi) {
    return this.appointmentsHttpService.sendAppointmentSuggestions(params);
  }

  public getClientKey(): string {
    return this.sandbox.getClientKey();
  }

  @Dispatch()
  public saveAppointmentsFilterData(
    appointmentsFilter: AppointmentsFilterData
  ): Observable<AppointmentsFilterAddedAction> {
    return of(new AppointmentsFilterAddedAction(appointmentsFilter));
  }
}
